// src/components/managed-sources/transformOutlookCopy.ts
//
// The one place that turns a transform outlook into words for a customer
// (plan 2026-08-02-transform-only-source-creation-dispatch-event, S6).
//
// Rules this file exists to keep:
//   - Customer vocabulary only. It is a "data model refresh" — never
//     "SQLMesh", never "transform", never "dispatch".
//   - No promise the backend cannot keep. A state names a number only when
//     the backend gave it a real clock: COOLDOWN always does, and since the
//     dispatch classes (plan 2026-08-08) SCHEDULED and WAITING_DAILY can too
//     — a refresh waiting for the rest of the morning's imports, or for the
//     daily 07:30 safety net, is hours away, and "usually within about 15
//     minutes" would be a promise the platform then misses every morning.
//     Without a clock, SCHEDULED stays soft: the tick cadence lives in
//     Dagster, not in the backend.
//   - Never claim "up to date" on top of a failure — that is what
//     NEEDS_ATTENTION is for — and never echo the raw error text.
//   - NOT_APPLICABLE (and anything unrecognised) says nothing at all: an
//     empty chip renders nothing and a null toast shows nothing.

import type { TransformOutlook } from "@/api/transformOutlook";

export interface OutlookCopy {
  /** Badge text next to Run History. Empty string means: render no chip. */
  chip: string;
  /** After-save toast body. `null` means: show no toast. */
  toast: string | null;
  /** Mantine colour for the badge and the notification. */
  color: string;
  /**
   * Secondary line: how many of the connector's sources haven't landed any
   * data yet, so their part of the last refresh rendered empty. Independent
   * of `state` — most useful underneath UP_TO_DATE, where "up to date" alone
   * would read as "done, nothing missing" over what is really an empty mart
   * waiting on its first import. `null` means: render nothing.
   */
  awaitingFirstDataLine: string | null;
}

/** Title of the after-save notification; the body comes from `outlookMessage`. */
export const OUTLOOK_TOAST_TITLE = "What happens next";

/**
 * States whose outcome is still moving, and therefore the only ones worth
 * polling for.
 *
 * Deliberately NOT `pending_change`: that flips to false the moment a run
 * starts, so polling on it would stop before "Up to date" ever appears.
 */
export const OUTLOOK_POLLING_STATES = [
  "RUNNING",
  "SCHEDULED",
  "COOLDOWN",
  "RETRYING",
] as const;

const HIDDEN: OutlookCopy = {
  chip: "",
  toast: null,
  color: "gray",
  awaitingFirstDataLine: null,
};

/** Closed enum from the backend; raw error text is never surfaced. */
const BLOCKED_REASON_COPY: Record<string, string> = {
  NO_CATEGORIES:
    "No data categories are enabled — enable at least one to refresh your data models.",
  NO_SOURCES: "No active sources to refresh.",
  WAREHOUSE_NOT_READY: "Your data warehouse is still being set up.",
  FIRST_IMPORT_PENDING: "Waiting for the first data import to finish.",
  FIRST_IMPORT_FAILED:
    "The first data import could not be completed. Check the source's status.",
};

const BLOCKED_FALLBACK = "Your data models are not refreshing right now.";

/** A RETRYING wait longer than this is honest about taking hours, not minutes. */
const LONG_RETRY_SECONDS = 3600;

/** Whole minutes, rounded up, never zero. `null` when there is nothing to count. */
function minutesRoundedUp(seconds: number | null | undefined): number | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.max(1, Math.ceil(seconds / 60));
}

function pluralMinutes(minutes: number): string {
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}

/**
 * Beyond this, a SCHEDULED refresh is not "about 15 minutes" any more and the
 * copy has to name the real wait. Sits above the backend's own soft tick
 * window (15 min) with a little slack, so an ordinary next-tick refresh keeps
 * the familiar wording and only a genuinely-held one changes it.
 */
const SCHEDULED_SOFT_CEILING_SECONDS = 20 * 60;

/**
 * A wait in customer words. Under 90 minutes it stays in minutes (rounded up,
 * so we are never late against our own copy); above that it rounds up to whole
 * hours — "about 2 hours" is honest where "about 97 minutes" is just precise.
 */
function waitPhrase(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes < 90) return `about ${pluralMinutes(minutes)}`;
  const hours = Math.ceil(minutes / 60);
  return hours === 1 ? "about 1 hour" : `about ${hours} hours`;
}

/**
 * Copy for the "waiting for first data" secondary line. `count` is absent on
 * a backend that predates the field, 0 once every source has landed
 * something, and `null` for both — there is nothing to render.
 */
function awaitingFirstDataLine(count: number | null | undefined): string | null {
  if (!count || count <= 0) return null;
  return count === 1
    ? "Waiting for first data from 1 source"
    : `Waiting for first data from ${count} sources`;
}

function lastRefreshedSuffix(iso: string | null | undefined): string {
  if (!iso) return "";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  const time = parsed.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return ` · last refreshed ${time}`;
}

/**
 * Turn an outlook into the chip text and the after-save toast body.
 *
 * Safe to call with `null`/`undefined` (nothing loaded yet) — it returns the
 * hidden shape rather than making the caller branch.
 */
export function outlookMessage(
  outlook: TransformOutlook | null | undefined,
): OutlookCopy {
  if (!outlook) return HIDDEN;

  return {
    ...stateCopy(outlook),
    awaitingFirstDataLine: awaitingFirstDataLine(outlook.sources_awaiting_first_data),
  };
}

/**
 * The state → {chip, toast, color} mapping, split out so `outlookMessage`
 * can attach the "waiting for first data" line uniformly across every
 * state — it is run-content, not part of the state's own decision, so it
 * must not be duplicated into each branch below.
 */
function stateCopy(outlook: TransformOutlook): Omit<OutlookCopy, "awaitingFirstDataLine"> {
  switch (outlook.state) {
    case "RUNNING":
      // A refresh already in flight cannot pick up a change made after it
      // started — say which run the customer's change lands in.
      return {
        chip: "Refreshing now",
        toast: outlook.pending_change
          ? "A refresh is running — your change will be included in the next one right after."
          : "Your change is included in the refresh that is running now.",
        color: "blue",
      };

    case "SCHEDULED": {
      // A refresh waiting for the rest of this morning's imports carries a
      // real upper bound from the backend. Use it once it is clearly past the
      // ordinary next-tick window — otherwise keep the familiar soft wording.
      const held = outlook.expected_within_seconds ?? 0;
      if (held > SCHEDULED_SOFT_CEILING_SECONDS) {
        return {
          chip: "Refresh scheduled",
          toast:
            "Your data models refresh once today's imports have finished — " +
            `within ${waitPhrase(held)}.`,
          color: "blue",
        };
      }
      return {
        chip: "Refresh scheduled",
        toast:
          "Your data models will refresh automatically — usually within about 15 minutes.",
        color: "blue",
      };
    }

    case "COOLDOWN": {
      const minutes = minutesRoundedUp(outlook.cooldown_seconds_remaining);
      if (minutes == null) {
        // Backend gave no remaining time — don't invent a number.
        return {
          chip: "Refresh scheduled",
          toast: "The next refresh starts shortly.",
          color: "blue",
        };
      }
      return {
        chip: `Refresh in ~${minutes} min`,
        toast: `The next refresh starts in about ${pluralMinutes(minutes)}.`,
        color: "blue",
      };
    }

    case "RETRYING": {
      // The backoff window can grow to hours, so the copy may never say
      // "in a few minutes" here.
      const long = (outlook.expected_within_seconds ?? 0) > LONG_RETRY_SECONDS;
      return {
        chip: "Waiting to retry",
        toast:
          "Waiting for a free slot — the refresh retries automatically." +
          (long ? " This can take a few hours." : ""),
        color: "yellow",
      };
    }

    case "WAITING_DAILY": {
      // The daily refresh now has a real time behind it (the backend owns the
      // clock — a fixed early-morning hour in the account's own timezone), so
      // say how long instead of the vague "next scheduled refresh".
      const wait = outlook.expected_within_seconds ?? 0;
      return {
        chip: "Scheduled for next run",
        toast:
          "Your change is saved and will be included in the next scheduled refresh" +
          (wait > 0 ? ` — within ${waitPhrase(wait)}.` : "."),
        color: "blue",
      };
    }

    case "BLOCKED":
      return {
        chip: "Refresh paused",
        toast:
          BLOCKED_REASON_COPY[outlook.blocked_reason ?? ""] ?? BLOCKED_FALLBACK,
        color: "orange",
      };

    case "NEEDS_ATTENTION":
      return {
        chip: "Needs attention",
        toast:
          "The last refresh failed. We retry automatically — check Run History for details.",
        color: "orange",
      };

    case "UP_TO_DATE":
      // Nothing happened and nothing is going to — a toast here would be noise.
      return {
        chip: `Up to date${lastRefreshedSuffix(outlook.last_refreshed_at)}`,
        toast: null,
        color: "gray",
      };

    case "NOT_APPLICABLE":
    default:
      // NOT_APPLICABLE: this connector never runs through the account-level
      // gate, so there is nothing truthful to say. An unknown state falls
      // here too — better silent than leaking an enum name.
      return HIDDEN;
  }
}

/** Whether this outlook is still moving and should keep being refreshed. */
export function isOutlookPolling(
  outlook: TransformOutlook | null | undefined,
): boolean {
  if (!outlook) return false;
  return (OUTLOOK_POLLING_STATES as readonly string[]).includes(outlook.state);
}
